import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { CLINICAL_TRIALS_DB_MAPPINGS } from '@shared/db-fields-mappings/clinical-trais-db.mappings';
import { SearchArea } from '@shared/enums/SearchArea';
import { TCenter } from '@shared/interfaces/TCenter';
import { TCtData } from '@shared/interfaces/TCtData';
import { THCP, TTHCPStudy } from '@shared/interfaces/THCP';
import { getUniqueByKey } from '@shared/utils/getUniqueByKey';
import { isNullOrEmpty } from '@shared/utils/isNullOrEmpty';
import { isNullOrUndefined } from '@shared/utils/isNullOrUndefined';
import { Subscription } from 'rxjs';
import {
    ICommonTableParams,
    IHandleCommonTable,
    PaginationVisibility,
    SortType,
    TCtDataTableRow,
    TTDataType,
    TTableRowType,
    TableRowType,
} from 'src/app/components/common-table/common-table.component.types';
import { ClinicalTrialsService } from 'src/app/services/clinical-trials.service';
import { HCPService } from 'src/app/services/hcp.service';
import { TDataColumnDef, buildColumnsDef } from 'src/utils/buildColumnsDef';
import { sortByDate } from 'src/utils/sort';
import { sortingDataAccessor } from 'src/utils/sortingDataAccessor';
import {
    TBaseComponentParams,
    TComponentType,
    isCenterParams,
    isHCPParams,
} from '../../../../interfaces/component-params.interface';

@Component({
  selector: 'app-studies',
  templateUrl: './studies.component.html',
  styleUrls: ['./studies.component.scss'],
})
export class StudiesComponent implements OnInit, IHandleCommonTable, OnDestroy {
  public dataSource: MatTableDataSource<TCtDataTableRow>;
  public clinicalTrialsData: TCtData[] = [];
  public columns: TDataColumnDef<TCtData>[] = [
    ...buildColumnsDef({
      textColumns: [],
      fieldsMappings: Array.from(CLINICAL_TRIALS_DB_MAPPINGS.values()).filter(
        (f) => f.visibleInSummary
      ),
      columnIdsWithLink: [`studyTitle`],
    }),
  ];

  @Input()
  public params: TBaseComponentParams = {
    categoryId: '',
    projectId: '',
    __type: TComponentType.hcp,
  };
  @Input()
  public hcpData: THCP | undefined = undefined;
  @Input()
  public centerData: TCenter | undefined = undefined;

  private _subs: Subscription[] = [];

  constructor(
    private _clinicalTrialsService: ClinicalTrialsService,
    private _HCPsService: HCPService
  ) {
    this.dataSource = new MatTableDataSource();
    this.dataSource.sortingDataAccessor = sortingDataAccessor;
    this._subs.push(
      this._clinicalTrialsService
        .getClinicalTrialsObservable()
        .subscribe((ctData) => {
          this.clinicalTrialsData = ctData;
          if (this.clinicalTrialsData.length > 0) {
            const dataColumns: TCtDataTableRow[] = this.clinicalTrialsData.map(
              (center, index) => {
                return {
                  ...center,
                  position: index + 1,
                  __type: TableRowType.CT,
                };
              }
            );
            this.dataSource.data = sortByDate(
              dataColumns,
              `studyStartYear`,
              `desc`
            );
          } else {
            this.dataSource.data = [];
          }
        })
    );
  }

  public ngOnDestroy() {
    this._subs.forEach((sub) => sub.unsubscribe());
  }

  public getTableParams(): ICommonTableParams<TTableRowType, TTDataType> {
    return {
      columns: this.columns as TDataColumnDef<TTDataType>[],
      dataCount: this.clinicalTrialsData.length,
      dataLoaded: true,
      dataSource: this.dataSource as MatTableDataSource<TTableRowType>,
      displayedColumns: Array.from(CLINICAL_TRIALS_DB_MAPPINGS.values())
        .filter((f) => f.visibleInSummary)
        .map((f) => f.fieldId),
      handleEditClick: () => void 0,
      pageIndex: 0,
      pageSize: 1000,
      pageSizeOptions: [10, 25, 50, 100],
      paginationCallback: () => void 0,
      downloadCallback: undefined,
      importCallback: undefined,
      area: SearchArea.None,
      projectId: this.params.projectId,
      selection: false,
      actionsAvailable: false,
      sortType: SortType.Simple,
      resize: true,
      handleNavigateToClick: (row: TTableRowType) =>
        this._handleNavigateToClick(<TCtDataTableRow>row),
      paginationVisibility: PaginationVisibility.Simple,
      simpleSortHeaders: [`Title`, `Condition`, `Study Start Date`],
      defaultSortFieldId: 'studyStartYear',
    };
  }

  public ngOnInit(): void {
    if (isHCPParams(this.params)) {
      if (
        !isNullOrUndefined(this.hcpData) &&
        !isNullOrEmpty(this.hcpData.first_name) &&
        !isNullOrEmpty(this.hcpData.last_name)
      ) {
        if (this.hcpData.studies.length > 0) {
          this._clinicalTrialsService.setClinicalTrialsByIDs(
            this.hcpData.studies.map((s) => s.id),
            this.params.projectId
          );
        } else {
          this._clinicalTrialsService.resetClinicalTrials();
        }
      }
    }
    if (isCenterParams(this.params)) {
      this._HCPsService.getHCPsForCenter(
        this.params.centerId,
        this.params.projectId,
        (results) => {
          const studies: TTHCPStudy[] = results
            .map((author) => {
              return author.studies;
            })
            .flat();
          const uniqueStudies: TTHCPStudy[] = getUniqueByKey(studies, `id`);
          if (uniqueStudies.length > 0) {
            this._clinicalTrialsService.setClinicalTrialsByIDs(
              uniqueStudies.map((s) => s.id),
              this.params.projectId
            );
          } else {
            this._clinicalTrialsService.resetClinicalTrials();
          }
        }
      );
    }
  }

  private _handleNavigateToClick(row: TCtDataTableRow): void {
    window.open(row.studyURL, '_blank')?.focus();
  }
}
